import React, { useEffect, useRef, useState } from "react";
import Datepicker from "react-tailwindcss-datepicker";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import { globalStorage, isValidDate } from "../../helpers";
// Import the weekday plugin
import weekday from "dayjs/plugin/weekday";

// Register the weekday plugin with dayjs
dayjs.extend(weekday);

export default function DatePicker({
  name,
  label,
  className,
  required,
  showShortcuts = false,
  values,
  useRange = true,
  single = false,
  onChange,
  displayFormat = "DD/MM/YYYY",
  errors,
  placeholder = "",
  disabled,
  minDate = null,
  maxDate = null,
  popoverDirection = "",
  primaryColor = "blue",
  separator = "~",
  isSubmit = false
}) {

  // Initialize with safe default values
  const [value, setValue] = useState({ startDate: new Date(), endDate: new Date() });
  const [dateValue, setDateValue] = useState({ minDate: null, maxDate: null });
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    let subscribe = false;
    if (!subscribe) {
      try {
        // Safely get year data with fallback
        const yearData = globalStorage?.getItem("fin_year_data") || null;

        // Create date objects with proper error handling
        let minDateYear = null;
        let maxDateYear = null;

        try {
          if (yearData) minDateYear = new Date(`${yearData}-01-01`);
          if (minDate) minDateYear = new Date(minDate);
        } catch (e) {
          console.error("Error setting minDate:", e);
          minDateYear = null;
        }

        try {
          if (yearData) maxDateYear = new Date(`${yearData}-12-31`);
          if (maxDate) maxDateYear = new Date(maxDate);
        } catch (e) {
          console.error("Error setting maxDate:", e);
          maxDateYear = null;
        }

        // Ensure dates are valid
        if (minDateYear && isNaN(minDateYear.getTime())) minDateYear = null;
        if (maxDateYear && isNaN(maxDateYear.getTime())) maxDateYear = null;

        setDateValue({ minDate: minDateYear, maxDate: maxDateYear });
      } catch (error) {
        console.error("Error in DatePicker useEffect:", error);
        setDateValue({ minDate: null, maxDate: null });
      }
    }

    return () => { subscribe = true; };
  }, [setDateValue, minDate, maxDate]);

  useEffect(() => {
    if (values && typeof values === "object") {
      setValue({
        startDate: isValidDate(values.startDate) ? new Date(values.startDate) : null,
        endDate: isValidDate(values.endDate) ? new Date(values.endDate) : null,
      });
    } else {
      setValue({ startDate: null, endDate: null });
    }
  }, [setValue, JSON.stringify(values)]);

  // handle input range
  const containerRef = useRef(null);
  const parseDateString = (dateStr, dateSeparator) => {
    if (!dateStr) return null;
    const normalized = dateStr.replaceAll(dateSeparator, "-");
    const [day, month, year] = normalized.split("-");

    if (!day || !month || !year) return null;
    const isoDate = `${year}-${month}-${day}`;
    const date = dayjs(isoDate);
    return date.isValid() ? date : null;
  }
  const handleDateChange = (event, _value) => {
    if (single) {
      // Single date mode
      const dateSeparator = _value.match(/[^0-9]/)?.[0] || null;
      const parsed = parseDateString(_value, dateSeparator);
      if (!parsed || !parsed.isValid()) return;

      const date = { startDate: parsed.toDate(), endDate: parsed.toDate() };
      setValue(date);
      onChange(date);

      event.target.value = dayjs(date.startDate).format(displayFormat);
    } else {
      // Range mode
      const parts = _value.split(separator).map((p) => p.trim());
      if (parts.length !== 2) return;

      const [startStr, endStr] = parts;
      const dateSeparator = startStr.match(/[^0-9]/)?.[0] || null;
      const start = parseDateString(startStr, dateSeparator);
      const end = parseDateString(endStr, dateSeparator);

      if (!start || !end || !start.isValid() || !end.isValid()) return;
      const date = { startDate: start.toDate(), endDate: end.toDate() };

      if (dayjs(date.endDate).isBefore(dayjs(date.startDate))) {
        [date.startDate, date.endDate] = [date.endDate, date.startDate];
      }

      setValue(date);
      onChange(date);

      const _start = dayjs(date.startDate).format(displayFormat);
      const _end = dayjs(date.endDate).format(displayFormat);
      event.target.value = `${_start} ${separator} ${_end}`;
    }
  }

  const onInputChange = (event) => {
    const key = event.key;
    const _value = event.target.value?.trim() || "";

    if (!isTyping) setIsTyping(true)

    // --- Handle clear input ---
    if (key === "Backspace" && _value === "") {
      const cleared = { startDate: null, endDate: null };
      setValue(cleared);
      onChange(cleared);
      return;
    }

    // --- Only trigger on Enter key ---
    if (key !== "Enter") return;
    handleDateChange(event, _value)
    if (isSubmit) event.target.form?.requestSubmit();
  };

  // on blur
  const onInputBlur = (event) => {
    const _value = event.target.value?.trim() || "";
    if (!_value) return;
    handleDateChange(event, _value)
  };


  // handle input single
  const handleChange = (date) => {
    if (isTyping) return;
    onChange?.(date);
  }

  useEffect(() => {
    const input = containerRef.current?.querySelector("input");
    if (!input) return;

    const handleFocus = () => setIsTyping(false);
    const handleBlur = (e) => {
      if (typeof onInputBlur === "function") onInputBlur(e);
      setTimeout(() => setIsTyping(false), 100);
    };
    const handleInput = (e) => {
      if (typeof onInputChange === "function") onInputChange(e);
    };

    input.addEventListener("keydown", handleInput);
    input.addEventListener("blur", handleBlur);
    input.addEventListener("focus", handleFocus);

    return () => {
      input.removeEventListener("keydown", handleInput);
      input.removeEventListener("blur", handleBlur);
      input.removeEventListener("focus", handleFocus);
    }
  }, [])


  // Safely create shortcut configurations
  const shortcutConfigs = {
    shortcuts: {
      today: {
        text: "Hari ini",
        period: {
          start: new Date(dayjs().format("YYYY-MM-DD")),
          end: new Date(dayjs().format("YYYY-MM-DD")),
        },
      },
      yesterday: "Kemarin",
      thisWeek: {
        text: "Minggu ini",
        period: {
          start: new Date(dayjs().startOf("week").format("YYYY-MM-DD")),
          end: new Date(dayjs().endOf("week").format("YYYY-MM-DD")),
        },
      },
      lastWeek: {
        text: "Minggu lalu",
        period: {
          start: new Date(
            dayjs().startOf("week").weekday(-7).format("YYYY-MM-DD")
          ),
          end: new Date(
            dayjs().startOf("week").weekday(-1).format("YYYY-MM-DD")
          ),
        },
      },
      past: (period) => `${period} hari terakhir`,
      currentMonth: "Bulan ini",
      pastMonth: "Bulan lalu",
      thisYear: {
        text: "Tahun ini",
        period: {
          start: new Date(dayjs().startOf("year").format("YYYY-MM-DD")),
          end: new Date(dayjs().endOf("year").format("YYYY-MM-DD")),
        },
      },
      lastYear: {
        text: "Tahun lalu",
        period: {
          start: new Date(
            dayjs().startOf("year").subtract(1, "year").format("YYYY-MM-DD")
          ),
          end: new Date(
            dayjs().startOf("year").subtract(1, "day").format("YYYY-MM-DD")
          ),
        },
      },
    },
  };

  // Safe check for values
  // const hasStartDate = values && values.startDate && ![null, "", " ", "undefined"].includes(values.startDate);
  const hasStartDate = !!values?.startDate;

  return (
    <div className="input-group-date relative" ref={containerRef}>
      <Datepicker
        i18n="id"
        popoverDirection={popoverDirection || "bottom"}
        inputId={name}
        minDate={dateValue.minDate}
        maxDate={dateValue.maxDate}
        useRange={useRange}
        showShortcuts={showShortcuts}
        configs={shortcutConfigs}
        separator={separator}
        primaryColor={primaryColor}
        containerClassName={"datepicker my-datepicker"}
        inputClassName={twMerge(
          "outline-none input-date",
          (hasStartDate && !errors) ? "has-value" : "",
          errors
            ? "border-[1.5px] border-red-500"
            : "border-[1.5px] border-gray-400",
          disabled ? "bg-gray-200" : "bg-transparent",
          disabled && hasStartDate && "disabled-value",
          className
        )}
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        displayFormat={displayFormat}
        onChange={handleChange}
        startFrom={values?.startDate ? new Date(values.startDate) : null}
        asSingle={single}
      />
      <label
        className={twMerge(
          `input-date-label`,
          errors ? "text-red-500" : "text-gray-500"
        )}
        htmlFor={name}
      >
        {label || ""}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    </div>
  );
}